import type { Responsive } from './utils';
import { DEVICES, atDevice } from './utils';

/**
 * Storing only what says something.
 *
 * An entity is compacted on every write — the migrations, the `create*`
 * factories, the editor's forms and the AI folds — so the persisted JSON holds
 * what differs from its defaults and nothing else. Every reader already falls
 * back to desktop (`atDevice`), and a group that is off reads as off, so a
 * compacted entity renders exactly like the expanded one.
 */

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

const deepEqual = (a: unknown, b: unknown): boolean => {
    if (Object.is(a, b)) return true;
    if (Array.isArray(a) && Array.isArray(b)) {
        return a.length === b.length && a.every((item, index) => deepEqual(item, b[index]));
    }
    if (!isPlainObject(a) || !isPlainObject(b)) return false;

    const keys = Object.keys(a);

    return keys.length === Object.keys(b).length && keys.every((key) => key in b && deepEqual(a[key], b[key]));
};

const DEVICE_KEYS = new Set<string>(DEVICES);

/** `{ desktop, tablet?, mobile? }` and nothing else. */
const isResponsive = (value: unknown): value is Responsive<unknown> =>
    isPlainObject(value) && 'desktop' in value && Object.keys(value).every((key) => DEVICE_KEYS.has(key));

/**
 * Drops `tablet` / `mobile` where they only repeat `desktop` — the value a reader
 * falls back to anyway. Hands back the same object when there is nothing to drop.
 */
export function compactResponsive<T>(responsive: Responsive<T>): Responsive<T> {
    const repeated = (['tablet', 'mobile'] as const).filter(
        (device) => device in responsive && deepEqual(responsive[device], responsive.desktop)
    );

    if (!repeated.length) {
        return responsive;
    }

    const kept = { ...responsive };
    repeated.forEach((device) => delete kept[device]);

    return kept;
}

/**
 * {@link compactResponsive} on every responsive leaf of a tree. Shares structure:
 * a subtree with nothing to drop comes back as the very same object, so a caller
 * comparing by reference still sees which groups an edit touched.
 *
 * `skipKeys` names the objects that are shaped like a responsive leaf but are
 * not one — a block's `hidden` is three switches read together, not a value
 * with a desktop fallback, so dropping its `tablet: false` would change what it
 * says.
 */
export function compactResponsiveTree<T>(value: T, skipKeys: ReadonlySet<string> = new Set()): T {
    if (Array.isArray(value)) {
        const items = value.map((item) => compactResponsiveTree(item, skipKeys));

        return (items.every((item, index) => item === value[index]) ? value : items) as T;
    }

    if (!isPlainObject(value)) {
        return value;
    }

    if (isResponsive(value)) {
        return compactResponsive(value) as T;
    }

    return mapValues(value, (child, key) => (skipKeys.has(key) ? child : compactResponsiveTree(child, skipKeys))) as T;
}

/** `Object.entries` → map → back, handing back the same object when no value changed. */
export function mapValues<T extends Record<string, unknown>>(
    value: T,
    map: (child: unknown, key: string) => unknown
): T {
    let changed = false;
    const next: Record<string, unknown> = {};

    for (const [key, child] of Object.entries(value)) {
        next[key] = map(child, key);
        changed ||= next[key] !== child;
    }

    return changed ? (next as T) : value;
}

/** Whether a per-breakpoint switch is off at every breakpoint, after the desktop fallback. */
export const isOffEverywhere = (isEnabled: Responsive<boolean> | null | undefined): boolean =>
    DEVICES.every((device) => !atDevice(isEnabled, device));
